import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import ImgAvatar from "../mui/ImgAvatar";
import CloseModal from "../mui/CloseModal";
import CusButton from "../mui/CusButton";
import IconCrown from "../../images/jsIcons/IconCrown";
import IconDiamond from "../../images/jsIcons/IconDiamond";
import IconCamera from "../../images/jsIcons/IconCamera";
import IconPrivate from "../../images/jsIcons/IconPrivate";
import OnlyMobileСarousel from "../mui/OnlyMobileСarousel";
import useJanus from "../../hooks/useJanus";
import { errorToast } from "../mui/Toaster";
import { ENUM_MODALS_VALUE, setModals } from "../../store/commonSlice";
import { useLocalStorage } from "usehooks-ts";
import QuestionPopup from "../mui/QuestionPopup";
import ErrorBoundary from "../mui/error_boundary/ErrorBoundary";
import ShowContainer from "../mui/ShowContainer";
import { ENUM_AVATAR_PLUG, ENUM_CUS_EVENT, TEST_MODE } from "../common/consts";
import { ENUM_CHAT_MODE, ENUM_LOCALSTORAGE } from "../common/enums";
import useBilling from "../../hooks/useBilling";
import { dispatchCusEvent } from "../../utils/utils";
import CusCheckbox from "../mui/custom_checkbox/CusCheckbox";

const CameraSetting = lazy(() => import("./CameraSetting"));

export const ENUM_CARD_ID = { private: "private", exclusive: "exclusive" };

const cardIdByChatMode = {
  [ENUM_CARD_ID.private]: ENUM_CHAT_MODE.private,
  [ENUM_CARD_ID.exclusive]: ENUM_CHAT_MODE.exclusive,
};

export default function ModalPrivate({ isOpen, closeModal }) {
  const ModalContent = () => {
    const { t } = useTranslation();
    const { changeChatMode, getSetting } = useJanus({});
    const [autostart, setAutostart] = useLocalStorage(ENUM_LOCALSTORAGE.autostart, false);

    const dispatch = useDispatch();
    const balance = useSelector((state) => state.user.user.balance);
    const broadCaster = useSelector((state) => state.chat.broadcaster);

    const { billing, describe } = useBilling();
    const { exclusiveBilling, privateBilling } = billing;
    const { exclusiveDescribe, privateDescribe } = describe;

    const [openCamera, setOpenCamera] = useState();

    const getInfoPrivate = (billing) => {
      const min = +(billing?.time?.value / 60).toFixed(1);

      return {
        min: min,
        price: billing?.price?.value * min,
        perPrice: billing?.price?.value,
      };
    };

    const privateInfo = useMemo(() => getInfoPrivate(privateBilling), [privateBilling]);
    const exclusiveInfo = useMemo(() => getInfoPrivate(exclusiveBilling), [exclusiveBilling]);

    const openNotify = () => {
      dispatch(setModals({ name: ENUM_MODALS_VALUE.notify, value: true }));
    };

    const depositAndClose = () => {
      dispatch(setModals({ name: ENUM_MODALS_VALUE.deposit, value: true }));
      closeModal();
    };

    useEffect(() => {
      if (isOpen) getSetting();
    }, [isOpen]);

    const onBtnClick = async (id) => {
      if (TEST_MODE) openNotify();

      if (id === ENUM_CARD_ID.exclusive && balance < exclusiveInfo.price) return depositAndClose();
      if (id === ENUM_CARD_ID.private && balance < privateInfo.price) return depositAndClose();

      changeChatMode(cardIdByChatMode[id])
        .then((e) => {
          if (!e) return;
          dispatchCusEvent(ENUM_CUS_EVENT.currentPremium, { mode: id });
          closeModal();
          openNotify();
        })
        .catch(errorToast);
    };

    const cards = [
      {
        id: ENUM_CARD_ID.exclusive,
        title: "Private Exclusive",
        icon: <IconDiamond />,
        checkbox: "Autostart of my camera and microphone",
        buttonText: `${t("Start")} ${exclusiveInfo.perPrice} ${t("tk")}/${t("min")}`,
        timeText: `${t("Minimum")} ${exclusiveInfo.min} ${t("min")}`,
        popupText:
          exclusiveInfo.min > 1 &&
          t("If you stop the show before minutes are up, you’ll be charged tk", {
            min: exclusiveInfo.min,
            paid: exclusiveInfo.price,
          }),
        advantage: [
          { icon: <IconCamera />, text: "Video call (Cam2Cam) availavle", color: "red" },
          { icon: <IconPrivate />, text: "No one can watch the show except you.", color: "white" },
        ],
        performancesText: "",
        performances: exclusiveDescribe,
      },
      {
        id: ENUM_CARD_ID.private,
        title: "Private Classic",
        icon: <IconCrown />,
        buttonText: `${t("Start")} ${privateInfo.perPrice} ${t("tk")}/${t("min")}`,
        timeText: `${t("Minimum")} ${privateInfo.min} ${t("min")}`,
        popupText:
          privateInfo.min > 1 &&
          t("If you stop the show before minutes are up, you’ll be charged tk", {
            min: privateInfo.min,
            paid: privateInfo.price,
          }),
        // advantage: [],
        performancesText: "",
        performances: privateDescribe,
      },
    ];

    return (
      <>
        <Modal
          ariaHideApp={false}
          className={`private-modal ${t("currentLang")}`}
          isOpen={isOpen}
          onRequestClose={closeModal}
        >
          <div className="private-modal__header">
            <h2 className="private-modal__title">{t("Start a Private show with")}</h2>
            <ImgAvatar
              id={broadCaster.id}
              plug={ENUM_AVATAR_PLUG.rabbit}
              avatarProps={broadCaster.imgSource}
            />
            <span className="private-modal__name">@{broadCaster.name}</span>
            <CloseModal close={closeModal} />
          </div>
          <OnlyMobileСarousel className="private-modal__content">
            {cards.map((card) => (
              <div key={card.id} className={`private-modal__card ${card.id}`}>
                <h3>{t(card.title)}</h3>
                <div className="private-modal__card__icon">{card.icon}</div>
                <CusButton onClick={() => onBtnClick(card.id)} color="red" text={card.buttonText} />
                <span className="private-modal__card__time">
                  {card.timeText}{" "}
                  <QuestionPopup className={"private-modal__card"} popupText={card.popupText} />
                </span>

                <ul className="private-modal__card__advantage">
                  <ShowContainer condition={card?.advantage}>
                    <li className={"red"}>
                      <IconCamera />
                      <span>{t("Video call (Cam2Cam) availavle")}</span>
                    </li>

                    <ShowContainer condition={card?.checkbox}>
                      <CusCheckbox
                        checked={autostart}
                        onChange={(e) => setAutostart(e.target.checked)}
                        id={"autostart"}
                        title={card?.checkbox}
                      />
                    </ShowContainer>

                    <li className={"white"}>
                      <IconPrivate />
                      <span>{t("No one can watch the show except you.")}</span>
                    </li>
                    <button onClick={() => setOpenCamera(true)} className="camera-setting">
                      {t("My camera and microphone")}
                    </button>
                  </ShowContainer>
                </ul>

                <ShowContainer condition={card.performances}>
                  <div className="private-modal__card__enjoy">
                    <div className="private-modal__card__enjoy__content">
                      <span className="private-modal__card__enjoy__item">
                        {t(card.performances)}
                      </span>
                    </div>
                  </div>
                </ShowContainer>
              </div>
            ))}
          </OnlyMobileСarousel>
          <p className="private-modal__p">
            {t("The model will be able to chat only with you and give you 100% attention.")}
          </p>
        </Modal>

        <Suspense>
          <CameraSetting
            buttonText={"Save"}
            isOpen={openCamera}
            closeModal={() => setOpenCamera(false)}
            // onSaveClick={onSaveClick}
          />
        </Suspense>
      </>
    );
  };

  return (
    <ErrorBoundary>
      <ModalContent />
    </ErrorBoundary>
  );
}
